/**
 * Custom Domain Types
 *
 * Data model for custom domain management.
 * Supports DNS authorization, SSL certificate provisioning via GCP Certificate Manager.
 *
 * URL Format (unified - no /w/ prefix):
 * - Custom domain: promo.client.com/summer-sale
 * - Default domain: api.segmently.ai/summer-sale
 */

/**
 * Status of custom domain setup process
 */
export type CustomDomainStatus =
  | 'pending_dns'       // User needs to add CNAME record
  | 'verifying_dns'     // Polling for DNS propagation
  | 'dns_verified'      // DNS verified, provisioning certificate
  | 'provisioning_cert' // Certificate being provisioned by GCP
  | 'configuring_lb'    // Adding to load balancer certificate map
  | 'active'            // Fully operational
  | 'failed'            // Permanent failure
  | 'suspended';        // Temporarily disabled

/**
 * GCP DNS Authorization state
 */
export type DnsAuthorizationState = 'AUTHORIZING' | 'AUTHORIZED' | 'FAILED';

/**
 * GCP Certificate state
 */
export type CertificateState = 'PROVISIONING' | 'ACTIVE' | 'FAILED' | 'PENDING_RENEWAL';

/**
 * GCP Certificate Map Entry state
 */
export type CertificateMapEntryState = 'ACTIVE' | 'PENDING' | 'FAILED';

/**
 * CNAME record details from GCP Certificate Manager
 */
export interface CnameRecord {
  /** DNS record name (e.g., "_acme-challenge.promo.client.com") */
  name: string;
  /** Record type (always "CNAME") */
  type: 'CNAME';
  /** Target value (e.g., "xxx.authorize.certificatemanager.goog") */
  data: string;
}

/**
 * A record for pointing domain to Load Balancer
 */
export interface ARecord {
  /** DNS record host (e.g., "promo.client.com") */
  host: string;
  /** Record type (always "A") */
  type: 'A';
  /** Load Balancer IP address */
  value: string;
}

/**
 * DNS records needed for custom domain setup
 * User must add BOTH records to their DNS provider
 */
export interface DnsRecords {
  /** A record: points domain to Load Balancer IP (for routing) */
  aRecord: ARecord;
  /** CNAME record: for SSL certificate verification (ACME challenge) */
  cnameRecord: {
    /** DNS record host (e.g., "_acme-challenge.promo.client.com") */
    host: string;
    /** Record type */
    type: 'CNAME';
    /** Target value (e.g., "xxx.authorize.certificatemanager.goog") */
    target: string;
  };
}

/**
 * DNS Authorization resource from GCP Certificate Manager
 */
export interface DnsAuthorization {
  /** GCP resource name (projects/{project}/locations/global/dnsAuthorizations/{id}) */
  name: string;
  /** CNAME record for domain verification */
  cnameRecord: CnameRecord;
  /** Current authorization state */
  state: DnsAuthorizationState;
  /** When DNS authorization was created */
  createdAt: string;
}

/**
 * Certificate resource from GCP Certificate Manager
 */
export interface Certificate {
  /** GCP resource name (projects/{project}/locations/global/certificates/{id}) */
  name: string;
  /** Current certificate state */
  state: CertificateState;
  /** Certificate expiration date (ISO timestamp) */
  expiresAt?: string;
  /** When certificate was created */
  createdAt: string;
}

/**
 * Certificate Map Entry resource from GCP Certificate Manager
 */
export interface CertificateMapEntry {
  /** GCP resource name */
  name: string;
  /** Current entry state */
  state: CertificateMapEntryState;
  /** When entry was created */
  createdAt: string;
}

/**
 * Error details for domain setup failures
 */
export interface CustomDomainError {
  /** Error code for programmatic handling */
  code: string;
  /** Human-readable error message */
  message: string;
  /** When error occurred (ISO timestamp) */
  timestamp: string;
}

/**
 * Serving surface of a custom domain (v2.20.0).
 * - 'web' (default, legacy docs without the field): root /{alias} serves
 *   personalized web funnels (SERVER_DATA, payments).
 * - 'article': root /{alias} serves the pure-CDN article layer (static,
 *   edge-cached; same handler as /a/{alias}).
 */
export type CustomDomainSurface = 'web' | 'article';

/**
 * Custom Domain entity stored in Firestore
 *
 * Collection: customDomains/{domainId}
 *
 * One custom domain per (project, surface): a project can hold ONE 'web'
 * domain and ONE 'article' domain simultaneously.
 * Domain maps to project, routing between aliases at the root path.
 */
export interface CustomDomain {
  /** Auto-generated document ID */
  id: string;

  /** Full domain (e.g., "promo.client.com") */
  domain: string;

  /** Reference to project */
  projectId: string;

  /** Serving surface; absent on legacy docs ⇒ 'web' */
  surface?: CustomDomainSurface;

  /** Current status in the verification pipeline */
  status: CustomDomainStatus;

  /** New format: both DNS records needed for setup */
  dnsRecords?: DnsRecords;

  /** DNS Authorization from GCP Certificate Manager (null until created) */
  dnsAuthorization: DnsAuthorization | null;

  /** Certificate from GCP Certificate Manager (null until provisioned) */
  certificate: Certificate | null;

  /** Certificate Map Entry for GLB routing (null until configured) */
  certificateMapEntry: CertificateMapEntry | null;

  /** When document was created (ISO timestamp) */
  createdAt: string;

  /** When document was last updated (ISO timestamp) */
  updatedAt: string;

  /** When DNS was verified (ISO timestamp) */
  verifiedAt?: string;

  /** When domain became fully active (ISO timestamp) */
  activatedAt?: string;

  /** User who created the domain */
  createdBy: string;

  /** Last error (if any) */
  lastError?: CustomDomainError;

  /** Article surface only: the article public base URL (path mode); absent/null = subdomain mode */
  publicBaseUrl?: string | null;

  /** Article surface only: the live article alias served at the site root; absent/null = none */
  homeAlias?: string | null;

  /** Article surface only: 'path' when `publicBaseUrl` is set (same reader as the builder, AC-044) */
  mode?: ArticleSiteMode;

  /** Article surface only: the project's mode switch job (running or last finished); null when none */
  modeSwitchJob?: ArticleModeSwitchJob | null;
}

/**
 * Article path mode (frontend mirror of builder-service ArticlePathModeService / ArticleModeSwitchJobStore).
 * The frontend never writes these; it reads them through /api/custom-domain/article-site and drives the job via `step`.
 */
export type ArticleSiteMode = 'subdomain' | 'path';

export interface ArticleModeSwitchResult {
  articleId: string;
  alias: string;
  status: 'republished' | 'skipped' | 'failed';
  /** skipped: unpublished edits (never pushed live by the switch) or retired after the switch started. */
  reason?: 'pending edits' | 'retired';
  note?: string;
  error?: string;
  warnings?: string[];
}

export type ArticleModeSwitchRelatedRefresh =
  | { ok: true; updated: number; unchanged: number; skipped: number }
  | { ok: false; error: string };

export type ArticleModeSwitchStatus = 'running' | 'completed' | 'completed_with_failures' | 'failed';

export interface ArticleModeSwitchJob {
  jobId: string;
  kind: 'set' | 'clear';
  publicBaseUrl: string | null;
  status: ArticleModeSwitchStatus;
  total: number;
  /** Article ids still to republish. */
  pending: string[];
  inFlight: { articleIds: string[]; claimedAt: string } | null;
  results: ArticleModeSwitchResult[];
  relatedRefresh: ArticleModeSwitchRelatedRefresh | null;
  warnings: string[];
  startedAt: string;
  heartbeatAt: string;
  finishedAt: string | null;
  finishingAt: string | null;
  startedBy: string | null;
}

/** GET /api/custom-domain/article-site and the body (`data`) of every path-mode write. */
export interface ArticlePathModeState {
  projectId: string;
  hasArticleDomain: boolean;
  articleHost: string | null;
  domainStatus: string | null;
  mode: ArticleSiteMode;
  publicBaseUrl: string | null;
  homeAlias: string | null;
  /** The project's mode switch job (running or last finished); null when none. */
  job: ArticleModeSwitchJob | null;
}

/** `base-url/verify` answer: read-only check, nothing is stored. */
export interface ArticleBaseUrlVerifyResult {
  ok: true;
  projectId: string;
  publicBaseUrl: string;
}

/** `base-url/clear` answer; `note` is set when the project was already in subdomain mode. */
export type ArticlePathModeClearResult = ArticlePathModeState & { note?: string };

/**
 * Create custom domain request
 */
export interface CreateCustomDomainRequest {
  /** Full domain (e.g., "promo.client.com") */
  domain: string;
  /** Project ID */
  projectId: string;
  /** User email */
  createdBy: string;
  /** Serving surface (default 'web') */
  surface?: CustomDomainSurface;
}

/**
 * Domain setup response with DNS record instructions
 */
export interface DomainSetupResponse {
  success: boolean;
  domainId: string;
  status: CustomDomainStatus;
  /** New format: both DNS records needed */
  dnsRecords: DnsRecords;
  /** Legacy field for backward compatibility */
  cnameRecord: {
    /** What user should add as DNS host (e.g., "_acme-challenge.promo") */
    host: string;
    /** What user should set as target */
    target: string;
  };
  instructions: string;
}

/**
 * Domain verification response
 */
export interface DomainVerifyResponse {
  success: boolean;
  status: CustomDomainStatus;
  message: string;
  retryAfter?: number; // seconds until next check recommended
}

/**
 * Domain status response
 */
export interface DomainStatusResponse {
  domain: string;
  status: CustomDomainStatus;
  progress: {
    dns: 'pending' | 'in_progress' | 'completed' | 'failed';
    certificate: 'pending' | 'in_progress' | 'completed' | 'failed';
    loadBalancer: 'pending' | 'in_progress' | 'completed' | 'failed';
  };
  estimatedCompletion?: string;
  error?: CustomDomainError;
}

/**
 * Domain mapping for GCS cache file
 */
export interface DomainMapping {
  projectId: string;
  status: 'active';
  /** Serving surface; absent on legacy entries ⇒ 'web' */
  surface?: CustomDomainSurface;
}

/**
 * GCS domain-mappings.json file structure
 */
export interface DomainMappingsFile {
  /** Increment on each update for cache busting */
  version: number;
  /** When file was last updated (ISO timestamp) */
  updatedAt: string;
  /** Domain to project mapping (only active domains) */
  mappings: Record<string, DomainMapping>;
}

/**
 * Cached domain in Project document
 */
export interface ProjectCustomDomain {
  /** Reference to customDomains/{domainId} */
  domainId: string;
  /** Cached domain for quick display */
  domain: string;
  /** Cached status for quick UI display */
  status: CustomDomainStatus;
}

/**
 * Validation constants
 */
export const CUSTOM_DOMAIN_VALIDATION = {
  /** Minimum domain length */
  MIN_LENGTH: 4, // a.co
  /** Maximum domain length */
  MAX_LENGTH: 253,
  /** Valid subdomain pattern (lowercase, numbers, hyphens, dots) */
  PATTERN: /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/,
  /** Reserved domains that cannot be used */
  RESERVED_DOMAINS: [
    'segmently.ai',
    'segmently.com',
    'api.segmently.ai',
    'dev-api.segmently.ai',
    'stage-api.segmently.ai',
  ],
  /** Maximum domains per (project, surface) pair — one 'web' + one 'article' */
  MAX_DOMAINS_PER_PROJECT: 1,
  /** DNS propagation timeout (7 days in ms) */
  DNS_PROPAGATION_TIMEOUT: 7 * 24 * 60 * 60 * 1000,
  /** Certificate provisioning timeout (24 hours in ms) */
  CERT_PROVISIONING_TIMEOUT: 24 * 60 * 60 * 1000,
} as const;

/**
 * Platform subdomains explicitly allowed to be bound as custom domains.
 *
 * The reserved-domain guard (RESERVED_DOMAINS, suffix-matched) blocks every
 * `*.segmently.ai` host so nobody can accidentally bind a platform host. These
 * Segmently-branded hosts are the narrow exception — checked BEFORE the reserved
 * guard so we can dogfood article publishing under our own brand. Prod-only:
 * dev/stage rehearsal goes through a domain we own that is not `*.segmently.ai`.
 *
 * IMPORTANT: keep this list in sync with the backend duplicate in
 * builder-service/src/services/customDomain/CustomDomainLifecycleService.ts
 * (the deploy-leak guard forbids builder-service importing runtime values from
 * @modules, so the backend keeps its own copy).
 */
export const ALLOWED_PLATFORM_SUBDOMAINS = [
  'help.segmently.ai',
  'articles.segmently.ai',
] as const;

/**
 * Error codes for custom domain operations
 */
export const CUSTOM_DOMAIN_ERROR_CODES = {
  INVALID_DOMAIN_FORMAT: 'INVALID_DOMAIN_FORMAT',
  DOMAIN_ALREADY_EXISTS: 'DOMAIN_ALREADY_EXISTS',
  PROJECT_LIMIT_REACHED: 'PROJECT_LIMIT_REACHED',
  DNS_NOT_PROPAGATED: 'DNS_NOT_PROPAGATED',
  DNS_VERIFICATION_FAILED: 'DNS_VERIFICATION_FAILED',
  CERTIFICATE_PROVISIONING_FAILED: 'CERTIFICATE_PROVISIONING_FAILED',
  CERTIFICATE_MAP_FAILED: 'CERTIFICATE_MAP_FAILED',
  DOMAIN_NOT_FOUND: 'DOMAIN_NOT_FOUND',
  UNAUTHORIZED: 'UNAUTHORIZED',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
} as const;

/**
 * Validate domain format
 */
export function isValidDomain(domain: string): boolean {
  if (!domain) return false;
  if (domain.length < CUSTOM_DOMAIN_VALIDATION.MIN_LENGTH) return false;
  if (domain.length > CUSTOM_DOMAIN_VALIDATION.MAX_LENGTH) return false;
  if (!CUSTOM_DOMAIN_VALIDATION.PATTERN.test(domain)) return false;

  const lowerDomain = domain.toLowerCase();
  // Cast to readonly string[] to allow string comparison against the `as const` tuple.
  const allowedPlatformSubdomains: readonly string[] = ALLOWED_PLATFORM_SUBDOMAINS;
  // Branded platform subdomains bypass the reserved guard (checked FIRST).
  if (allowedPlatformSubdomains.includes(lowerDomain)) return true;
  // Suffix match (host itself or any subdomain of a reserved zone) — keep in sync
  // with the backend duplicate in CustomDomainLifecycleService.isValidDomain.
  if (
    CUSTOM_DOMAIN_VALIDATION.RESERVED_DOMAINS.some(
      r => lowerDomain === r || lowerDomain.endsWith(`.${r}`),
    )
  ) {
    return false;
  }
  return true;
}

/**
 * Sanitize domain for use as GCP resource ID
 * Replaces dots with dashes, ensures lowercase
 */
export function sanitizeDomainForResourceId(domain: string): string {
  return domain.toLowerCase().replace(/\./g, '-');
}

/**
 * Extract subdomain host from full domain
 * e.g., "promo.client.com" → "promo"
 */
export function extractSubdomainHost(domain: string): string {
  const parts = domain.split('.');
  return parts.length > 2 ? parts[0] : domain;
}
